import { useGraphs } from "../../context/GraphsProvider";
import { GraphType } from "../../types";
import Loader from "../AppLoader";
import Error from "../Error";
import GraphItem from "./GraphsGrid/GraphItem";
import SingleGraphView from "./GraphsGrid/SingleGraphView";
import { twMerge } from "tailwind-merge";
import { useGraphEnlarge } from "../../context/GraphEnlargeContext";

type BreakpointRows = {
    secondInRow: number[];
    thirdInRow: number[];
    forthInRow: number[];
};

type Layouts = Partial<Record<GraphType, BreakpointRows>>;

const layouts: Layouts = {
    "inbar-12": {
        // 4 X 3
        secondInRow: [1, 5, 9],
        thirdInRow: [2, 6, 10],
        forthInRow: [3, 7, 11],
    },
    "wasserman-9": {
        // 3 X 3
        secondInRow: [1, 4, 8],
        thirdInRow: [2, 5, 9],
        forthInRow: [-1],
    },
};

export default function GraphsGrid() {
    const { graphIdToEnlarge, setGraphIdToEnlarge } = useGraphEnlarge();
    const { graphs, loading, error, graphsType } = useGraphs();

    if (loading)
        return (
            <div className="w-full h-[700px] bg-white grid place-content-center">
                <Loader />
            </div>
        );

    if (error) return <Error />;

    if (!graphs) return null;

    const isPartialMode = graphs.length <= 4;
    const layout = layouts[graphsType!]!;

    if (graphIdToEnlarge) {
        return (
            <SingleGraphView
                graphIdToEnlarge={graphIdToEnlarge}
                setGraphIdToEnlarge={setGraphIdToEnlarge}
            />
        );
    }

    return (
        <div
            id={`graphs-grid-${graphsType}`}
            className={twMerge(
                "grid",
                isPartialMode
                    ? "grid-cols-2 w-full max-w-[850px] xl:max-w-[1000px] 2xl:max-w-[1200px] mx-auto gap-y-2.5 gap-x-8 xl:gap-x-20"
                    : `justify-between ${
                          graphsType === "inbar-12"
                              ? "grid-cols-4 gap-y-[5px] gap-x-4 xl:gap-x-8 mx-auto xl:max-w-[1400px]"
                              : "grid-cols-3 gap-x-8 w-full max-w-[980px] xl:max-w-[1100px] 2xl:max-w-[1150px] mx-auto gap-y-0.5"
                      }`
            )}
        >
            {graphs.length === 0 && <div>No graphs to display</div>}
            {graphs.map((graphData, index) => {
                const isSecondInRow = layout.secondInRow.includes(index);
                const isThirdInRow = layout.thirdInRow.includes(index);
                const isFourthInRow = layout.forthInRow.includes(index);

                const isLeftGraph = isPartialMode
                    ? isSecondInRow
                    : graphsType === "inbar-12"
                    ? isFourthInRow
                    : isThirdInRow;

                return (
                    <GraphItem
                        key={graphData.graph_id}
                        graphData={graphData}
                        isPartialMode={isPartialMode}
                        isLeftGraph={isLeftGraph}
                    />
                );
            })}
        </div>
    );
}
